import React, { useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

import { useViewerData } from '@xingternal/hub';

import { zeroifySearchAlertsNewJobCount } from '@/domain/search-alerts/helpers';
import { useVisitSearchAlertMutation } from '@/graphql/generated';
import type { JobRecommendationFragmentWithVisibleJob } from '@/types';

import { useSearchAlertsContext } from '../../contexts';
import { isVisibleJob } from '../../helpers';
import { useSearch } from '../../hooks';
import type { JobItemResultFragmentWithVisibleJob } from '../../types';
import { EmptyState } from './empty-state';
import { ResultsHeader } from './header';
import { ResultsList } from './list';
import { Pagination } from './pagination';
import { ResultsSkeleton } from './skeleton';

type IsNew = {
  isNew?: boolean;
};

export type Item = (JobItemResultFragmentWithVisibleJob | JobRecommendationFragmentWithVisibleJob) & IsNew;

const SEARCH_ALERT_COUNT_QUERY_PARAM = 'searchAlertCount';
const ORIGIN_QUERY_PARAM = 'origin';
const SEARCH_ALERT_EMAIL_QUERY_PARAM = 'referrer_alert_id';
const ORIGIN_QUERY_PARAM_SEARCH_ALERTS_VALUE = 'search_alerts';
const ORIGIN_QUERY_PARAM_SEARCH_ALERTS_LANDING_VALUE = 'search_alerts_landing';

export const Results: React.FC = () => {
  const { data, loading } = useSearch();
  const location = useLocation();
  const history = useHistory();
  const { data: userData } = useViewerData();
  const userId = userData?.user?.id;

  const [{ origin, searchAlertCount, referrer_alert_id: referrerAlertId }] = useQueryParams({
    [ORIGIN_QUERY_PARAM]: StringParam,
    [SEARCH_ALERT_EMAIL_QUERY_PARAM]: NumberParam,
    [SEARCH_ALERT_COUNT_QUERY_PARAM]: NumberParam,
  });
  const { alertId } = useSearchAlertsContext();
  const [visitSearchAlertMutation] = useVisitSearchAlertMutation();

  const markAlertAsVisited =
    origin === ORIGIN_QUERY_PARAM_SEARCH_ALERTS_VALUE ||
    origin === ORIGIN_QUERY_PARAM_SEARCH_ALERTS_LANDING_VALUE ||
    referrerAlertId;

  const removeRefParam = useCallback((): void => {
    const params = new URLSearchParams(location.search);

    if (params.has(ORIGIN_QUERY_PARAM)) {
      params.delete(ORIGIN_QUERY_PARAM);
    }
    if (params.has(SEARCH_ALERT_EMAIL_QUERY_PARAM)) {
      params.delete(SEARCH_ALERT_EMAIL_QUERY_PARAM);
    }

    history.replace({ search: params.toString() });
  }, [location, history]);

  useEffect(() => {
    if (alertId && markAlertAsVisited) {
      visitSearchAlertMutation({
        variables: { id: alertId },
        update: zeroifySearchAlertsNewJobCount(alertId, userId),
      });

      removeRefParam();
    }
  }, [alertId, markAlertAsVisited, removeRefParam, userId, visitSearchAlertMutation]);

  if (loading) return <ResultsSkeleton />;
  if (!data?.jobSearchByQuery?.collection) return <EmptyState hasError />;

  const addIsNewProp = (job: Item, index: number): Item => {
    return {
      ...job,
      ...(searchAlertCount && index < searchAlertCount && { isNew: true }),
    };
  };

  const filteredCollection = data.jobSearchByQuery.collection.filter(isVisibleJob).map(addIsNewProp);

  return filteredCollection.length > 0 ? (
    <>
      <ResultsHeader />
      <ResultsList items={filteredCollection} />
      <Pagination />
    </>
  ) : (
    <EmptyState />
  );
};
